import {
  ArrowRight,
  Building2,
  CalendarClock,
  CalendarRange,
  ChevronRight,
  CircleCheck,
  CircleMinus,
  Circle,
  Copy,
  ExternalLink,
  Flag,
  Globe,
  Image,
  KeyRound,
  Languages,
  Lock,
  Mail,
  MapPin,
  Megaphone,
  Phone,
  Quote,
  Rocket,
  ShieldAlert,
  ShieldCheck,
  StickyNote,
  Timer,
  TrendingUp,
  User,
  type LucideIcon,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { Routes } from '../../app/routes';
import type { Contact, EmployeeSummary, ItemCheck, ItemLink, ItemPayload, Metric, PayloadOf, PriceLine } from '../../domain/models';
import { contactDisplay, mailDraft, manualText, oneOffTotal, pasteText, targetUri } from '../../domain/payload';
import { useLocale, useT } from '../../l10n/i18n';
import {
  accessKind,
  adPlatformName,
  channelName,
  checkStyle,
  formatCurrency,
  formatMoney,
  itemKindLabel,
  sourceKind,
} from '../core/presentation';
import { EmployeeAvatar } from '../core/widgets/EmployeeAvatar';
import { Pill } from '../core/widgets/common';
import { LinkedText, LinkTile, PlaceTile, TileList, outside, prettyUrl, safeUrl, useCopy } from './links';
import { MiniMarkdown } from './MiniMarkdown';
import { KindGlyph } from './parts';

// An item's content by schema, ported from the mobile app's payload_views.dart. With `onChange`, the
// fields a person may change before approving become inputs.

export interface PayloadViewProps {
  payload: ItemPayload;
  /** Editing: the changed payload, field by field. */
  onChange?: (p: ItemPayload) => void;
  workspaceName?: string;
  /** Shown inside another item (the work a task came from) rather than on its own page. */
  embedded?: boolean;
  /** Untrusted content already let in, by a person or automatically after the check: AI may read it now. */
  letIn?: boolean | 'automatic';
  /** Untrusted content: what the isolated check made of it, and something to do about it (ADR 0011). */
  check?: ItemCheck;
  checkAction?: ReactNode;
  /** Finds a colleague by name, for payloads that name one (who asks for access). */
  employeeNamed?: (name: string) => EmployeeSummary | undefined;
  /** The item's links. Posts and messages show them where the work goes; see `takesLinks`. */
  links?: ItemLink[];
  /** A person carries the work out, so a message's address opens as a ready email. */
  manual?: boolean;
  /** Approved work the person still has to carry out: opening where it goes copies the text too. */
  awaitsManual?: boolean;
}

/** Whether this payload shows the item's links itself, next to where the work goes. */
export const takesLinks = (p: ItemPayload) => p.type === 'social_post' || p.type === 'message_reply';

/** Where a post or message goes: the item's links, whether a person sends it, and what opening one of
 * them does besides (copy the text to paste there). */
interface Where {
  links: ItemLink[];
  manual: boolean;
  onOpen?: () => void;
}

export function PayloadView(props: PayloadViewProps) {
  const t = useT();
  const copy = useCopy();
  const { payload, onChange } = props;
  const text = manualText(payload);
  const done = payload.type === 'message_reply' ? t('actionSent') : t('actionPublished');
  const where: Where = {
    links: props.links ?? [],
    manual: props.manual ?? false,
    onOpen: props.awaitsManual && text !== undefined ? () => copy(text, t('copiedPasteIt', { done })) : undefined,
  };
  switch (payload.type) {
    case 'social_post':
      return <SocialPostView p={payload} onChange={onChange} workspaceName={props.workspaceName} where={where} />;
    case 'offer':
      return <OfferView p={payload} onChange={onChange} />;
    case 'message_reply':
      return <MessageReplyView p={payload} onChange={onChange} where={where} />;
    case 'deliverable':
      return <DeliverableView p={payload} onChange={onChange} />;
    case 'task':
      return <TaskView p={payload} workspaceName={props.workspaceName} />;
    case 'access_request':
      return (
        <AccessRequestView
          p={payload}
          requester={payload.requestedBy ? props.employeeNamed?.(payload.requestedBy) : undefined}
        />
      );
    case 'inbound_message':
      return (
        <InboundMessageView
          p={payload}
          embedded={props.embedded ?? false}
          letIn={props.letIn ?? false}
          check={props.check}
          checkAction={props.checkAction}
        />
      );
    case 'report':
      return <ReportView p={payload} />;
    case 'ad_change':
      return <AdChangeView p={payload} />;
    case 'document':
      return <DocumentView p={payload} onChange={onChange} />;
    case 'unknown':
      return (
        <ItemSection title={t('sectionUnknown')}>
          <pre className="panel iv-json">{JSON.stringify(payload.raw, null, 2)}</pre>
        </ItemSection>
      );
    case 'hidden':
      return (
        <ItemSection title={t('sectionUnknown')}>
          <p className="body-medium muted">{t('hiddenContent')}</p>
        </ItemSection>
      );
  }
}

// ---- Building blocks ----

/** A titled block of the item page. */
export function ItemSection({ title, trailing, children }: { title: string; trailing?: ReactNode; children: ReactNode }) {
  return (
    <section className="iv-section">
      <div className="iv-section-head">
        <h2 className="overline">{title}</h2>
        {trailing}
      </div>
      {children}
    </section>
  );
}

function CopyButton({ text, label }: { text: string; label?: string }) {
  const t = useT();
  const copy = useCopy();
  const name = label ?? t('actionCopy');
  return (
    <button type="button" className="icon-btn iv-copy" aria-label={name} title={name} onClick={() => copy(text)}>
      <Copy size={18} aria-hidden />
    </button>
  );
}

/** Text that turns into an input while editing. */
function EditableBlock({
  value,
  onChange,
  label,
  large = true,
  rows = 3,
}: {
  value: string;
  onChange?: (v: string) => void;
  /** Names the field for screen readers. */
  label: string;
  large?: boolean;
  rows?: number;
}) {
  if (!onChange) return <p className={`${large ? 'body-large' : 'body-medium'} pre-wrap`}>{value}</p>;
  return (
    <textarea
      className={`textarea iv-edit ${large ? 'body-large' : 'body-medium'}`}
      value={value}
      rows={rows}
      aria-label={label}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

function Bullets({ items, icon: Icon, tone }: { items: string[]; icon: LucideIcon; tone: string }) {
  return (
    <ul className="iv-bullets">
      {items.map((s, i) => (
        <li key={i}>
          <Icon size={18} color={tone} aria-hidden />
          <span className="body-medium">{s}</span>
        </li>
      ))}
    </ul>
  );
}

function ContactLines({ contact }: { contact: Contact }) {
  const rows: [LucideIcon, string][] = [];
  if (contact.name) rows.push([User, contact.name]);
  if (contact.company) rows.push([Building2, contact.company]);
  if (contact.email) rows.push([Mail, contact.email]);
  if (contact.phone) rows.push([Phone, contact.phone]);
  return (
    <div className="iv-contact">
      {rows.map(([Icon, text]) => (
        <div key={text} className="iv-contact-row">
          <Icon size={16} aria-hidden />
          <span className="body-medium">{text}</span>
        </div>
      ))}
    </div>
  );
}

const hasContact = (c: Contact) => contactDisplay(c).length > 0 || c.email !== undefined;

function NotesBlock({ notes }: { notes: string }) {
  const t = useT();
  return (
    <ItemSection title={t('sectionNotes')}>
      <div className="panel iv-note">
        <StickyNote size={18} aria-hidden />
        <p className="body-medium pre-wrap">
          <LinkedText text={notes} />
        </p>
      </div>
    </ItemSection>
  );
}

/** The item's links as tiles, leaving out an address already shown above them. */
function linkTiles(where: Where, shown?: URL): ReactNode[] {
  return where.links.flatMap((l, i) => {
    const u = safeUrl(l.url);
    return u && u.href !== shown?.href ? [<LinkTile key={`link-${i}`} label={l.label} url={u} onOpen={where.onOpen} />] : [];
  });
}

function useDates() {
  const locale = useLocale();
  return {
    dayTime: (d: Date) =>
      new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }).format(d),
    day: (d: Date) => new Intl.DateTimeFormat(locale, { year: 'numeric', month: 'short', day: 'numeric' }).format(d),
  };
}

// ---- social_post.v1 ----

function SocialPostView({
  p,
  onChange,
  workspaceName,
  where,
}: {
  p: PayloadOf<'social_post'>;
  onChange?: (p: ItemPayload) => void;
  workspaceName?: string;
  where: Where;
}) {
  const t = useT();
  const dates = useDates();
  const uri = safeUrl(targetUri(p));
  const places: ReactNode[] = [];
  if (uri) places.push(<LinkTile key="target" label={p.target ?? prettyUrl(uri)} url={uri} onOpen={where.onOpen} />);
  else if (p.target) places.push(<PlaceTile key="target" label={p.target} detail={channelName(p.channel)} icon={MapPin} />);
  places.push(...linkTiles(where, uri));
  const editing = onChange !== undefined;
  const initial = [...(workspaceName ?? '?')][0] ?? '?';

  return (
    <div>
      {places.length > 0 && (
        <ItemSection title={t('sectionWherePost')}>
          <TileList>{places}</TileList>
        </ItemSection>
      )}
      {(p.publishAt || p.language) && (
        <div className="iv-pills iv-gap-lg">
          {p.publishAt && <Pill label={dates.dayTime(p.publishAt)} icon={CalendarClock} />}
          {p.language && <Pill label={p.language.toUpperCase()} icon={Languages} />}
        </div>
      )}
      <ItemSection title={t('sectionPost')} trailing={editing ? undefined : <CopyButton text={pasteText(p)} />}>
        <div className="panel">
          <div className="iv-post-author">
            <span className="iv-post-initial label-medium" aria-hidden>
              {initial}
            </span>
            <span className="title-small">{workspaceName ?? ''}</span>
          </div>
          {p.headline && (
            <div className="iv-post-headline">
              <p className="title-large">{p.headline}</p>
              {/* A title is pasted on its own (Reddit), so it has its own copy button. */}
              {!editing && <CopyButton text={p.headline} />}
            </div>
          )}
          <EditableBlock
            value={p.text}
            label={t('sectionPost')}
            rows={6}
            onChange={onChange && ((text) => onChange({ ...p, text }))}
          />
          {p.link && !p.text.includes(p.link) && (
            <p className="body-medium iv-gap-top">
              <LinkedText text={p.link} />
            </p>
          )}
          {p.media.map((m, i) => (
            <div key={i} className="iv-media body-small">
              <Image size={16} aria-hidden />
              <span className="ellipsis">{m}</span>
            </div>
          ))}
        </div>
      </ItemSection>
      {p.firstComment !== undefined && (
        <ItemSection
          title={t('sectionFirstComment')}
          trailing={editing ? undefined : <CopyButton text={p.firstComment} />}
        >
          <div className="panel">
            <EditableBlock
              value={p.firstComment}
              label={t('sectionFirstComment')}
              large={false}
              rows={2}
              onChange={onChange && ((firstComment) => onChange({ ...p, firstComment }))}
            />
          </div>
        </ItemSection>
      )}
      {p.notes && <NotesBlock notes={p.notes} />}
    </div>
  );
}

// ---- offer.v1 ----

function OfferView({ p, onChange }: { p: PayloadOf<'offer'>; onChange?: (p: ItemPayload) => void }) {
  const t = useT();
  const editing = onChange !== undefined;
  const indexed = p.prices.map((line, i) => ({ line, i }));
  const core = indexed.filter((x) => !x.line.optional);
  const options = indexed.filter((x) => x.line.optional);

  const rows = (lines: typeof indexed) =>
    lines.map(({ line, i }) => (
      <PriceRow
        key={`${line.key}-${i}`}
        line={line}
        currency={p.currency}
        onChange={
          onChange &&
          ((amount) => {
            const prices = [...p.prices];
            prices[i] = { ...line, amount };
            onChange({ ...p, prices });
          })
        }
      />
    ));

  return (
    <div>
      {hasContact(p.client) && (
        <ItemSection title={t('sectionClient')}>
          <div className="panel">
            <ContactLines contact={p.client} />
          </div>
        </ItemSection>
      )}
      <ItemSection title={t('sectionSummary')}>
        <EditableBlock
          value={p.summary}
          label={t('sectionSummary')}
          onChange={onChange && ((summary) => onChange({ ...p, summary }))}
        />
      </ItemSection>
      {p.scope.length > 0 && (
        <ItemSection title={t('sectionScope')}>
          <Bullets items={p.scope} icon={CircleCheck} tone="var(--success)" />
        </ItemSection>
      )}
      {p.outOfScope.length > 0 && (
        <ItemSection title={t('sectionOutOfScope')}>
          <Bullets items={p.outOfScope} icon={CircleMinus} tone="var(--ink-faint)" />
        </ItemSection>
      )}
      {p.acceptanceCriteria.length > 0 && (
        <ItemSection title={t('sectionCriteria')}>
          <Bullets items={p.acceptanceCriteria} icon={Flag} tone="var(--accent)" />
        </ItemSection>
      )}
      <ItemSection title={t('sectionPrices')}>
        <div className="panel iv-prices">
          {rows(core)}
          <div className="iv-price-total">
            <span className="title-medium">{t('priceTotal')}</span>
            <span className="headline-small">{formatMoney(oneOffTotal(p), p.currency)}</span>
          </div>
          {options.length > 0 && (
            <>
              <div className="overline iv-price-options">{t('priceOptions')}</div>
              {rows(options)}
            </>
          )}
        </div>
      </ItemSection>
      {(editing || (p.message?.length ?? 0) > 0) && (
        <ItemSection title={t('sectionMessageToClient')}>
          <EditableBlock
            value={p.message ?? ''}
            label={t('sectionMessageToClient')}
            onChange={onChange && ((message) => onChange({ ...p, message }))}
          />
        </ItemSection>
      )}
      {p.notes && <NotesBlock notes={p.notes} />}
    </div>
  );
}

const amountText = (n: number) => (Number.isInteger(n) ? n.toFixed(0) : String(n));

function PriceRow({ line, currency, onChange }: { line: PriceLine; currency: string; onChange?: (amount: number) => void }) {
  const t = useT();
  // What the person typed, kept as typed ("12," on the way to "12,5").
  const [typed, setTyped] = useState(() => amountText(line.amount));
  const per = line.monthly ? t('perMonth') : '';
  return (
    <div className="iv-price-row">
      <span className="body-medium">{line.label}</span>
      {onChange ? (
        <span className="iv-price-input">
          <input
            className="input title-medium"
            inputMode="decimal"
            value={typed}
            aria-label={line.label}
            onChange={(e) => {
              const v = e.target.value.replace(/[^0-9.,]/g, '');
              setTyped(v);
              const n = Number.parseFloat(v.replace(',', '.'));
              if (!Number.isNaN(n) && n >= 0) onChange(n);
            }}
          />
          <span className="title-small muted">
            {currency === 'EUR' ? '€' : currency}
            {per}
          </span>
        </span>
      ) : (
        <span className="title-medium iv-nowrap">
          {formatMoney(line.amount, currency)}
          {per}
        </span>
      )}
    </div>
  );
}

// ---- message_reply.v1 ----

function MessageReplyView({
  p,
  onChange,
  where,
}: {
  p: PayloadOf<'message_reply'>;
  onChange?: (p: ItemPayload) => void;
  where: Where;
}) {
  const t = useT();
  const to = p.to;
  const name = contactDisplay(to) || to.email || to.phone || '';
  const email = to.email;
  const draft = where.manual && email ? safeUrl(mailDraft(email, p.subject, p.text)) : undefined;
  const places: ReactNode[] = [];
  // The person sends it by hand: the address opens their mail app with the message written.
  if (draft) places.push(<LinkTile key="to" label={name} url={draft} onOpen={where.onOpen} />);
  else if (name) {
    const detail = [email === name ? undefined : email, to.phone, channelName(p.channel)].filter(Boolean).join(' · ');
    places.push(<PlaceTile key="to" label={name} detail={detail} icon={email ? Mail : User} />);
  }
  places.push(...linkTiles(where));

  return (
    <div>
      {places.length > 0 && (
        <ItemSection title={t('sectionTo')}>
          <TileList>{places}</TileList>
        </ItemSection>
      )}
      {p.inReplyTo && (
        <ItemSection title={t('sectionInReplyTo')}>
          <blockquote className="iv-quote body-medium muted pre-wrap">{p.inReplyTo}</blockquote>
        </ItemSection>
      )}
      {p.subject && <p className="title-medium iv-subject">{p.subject}</p>}
      <ItemSection title={p.inReplyTo ? t('sectionReply') : t('sectionMessage')}>
        <EditableBlock
          value={p.text}
          label={p.inReplyTo ? t('sectionReply') : t('sectionMessage')}
          rows={6}
          onChange={onChange && ((text) => onChange({ ...p, text }))}
        />
      </ItemSection>
      {p.notes && <NotesBlock notes={p.notes} />}
    </div>
  );
}

// ---- deliverable.v1 ----

function DeliverableView({ p, onChange }: { p: PayloadOf<'deliverable'>; onChange?: (p: ItemPayload) => void }) {
  const t = useT();
  const editing = onChange !== undefined;
  const done = p.checklist.filter((c) => c.done).length;
  return (
    <div>
      <PreviewCard title={p.title} url={p.url} />
      {p.summary && (
        <ItemSection title={t('sectionWhatWasDone')}>
          <p className="body-large pre-wrap">{p.summary}</p>
        </ItemSection>
      )}
      {p.checklist.length > 0 && (
        <ItemSection
          title={t('sectionChecklist')}
          trailing={
            <span
              className="label-small"
              style={{ color: done === p.checklist.length ? 'var(--success)' : 'var(--warning)' }}
            >
              {done}/{p.checklist.length}
            </span>
          }
        >
          <ul className="panel iv-checklist">
            {p.checklist.map((c, i) => (
              <li key={i} className={c.done ? 'done' : undefined}>
                {c.done ? (
                  <CircleCheck size={20} color="var(--success)" aria-hidden />
                ) : (
                  <Circle size={20} color="var(--ink-faint)" aria-hidden />
                )}
                <span className="body-medium">{c.text}</span>
              </li>
            ))}
          </ul>
        </ItemSection>
      )}
      {hasContact(p.client) && (
        <ItemSection title={t('sectionClient')}>
          <div className="panel">
            <ContactLines contact={p.client} />
          </div>
        </ItemSection>
      )}
      {(editing || (p.message?.length ?? 0) > 0) && (
        <ItemSection title={t('sectionMessageToClient')}>
          <EditableBlock
            value={p.message ?? ''}
            label={t('sectionMessageToClient')}
            onChange={onChange && ((message) => onChange({ ...p, message }))}
          />
        </ItemSection>
      )}
      {p.notes && <NotesBlock notes={p.notes} />}
    </div>
  );
}

/** The finished work as a little browser window: where it lives and a way to open it. */
function PreviewCard({ title, url }: { title: string; url: string }) {
  const t = useT();
  const copy = useCopy();
  const u = safeUrl(url);
  const openable = u && u.protocol !== 'mailto:' ? u : undefined;
  return (
    <div className="iv-preview">
      <div className="iv-preview-bar">
        <span className="iv-preview-dots" aria-hidden>
          <i />
          <i />
          <i />
        </span>
        <span className="iv-preview-address label-small">
          {u?.protocol === 'https:' ? <Lock size={12} aria-hidden /> : <Globe size={12} aria-hidden />}
          <span className="ellipsis">{u?.hostname || url}</span>
        </span>
      </div>
      <div className="iv-preview-body">
        <div className="overline iv-accent iv-preview-label">
          <Rocket size={15} aria-hidden />
          {t('sectionPreview')}
        </div>
        <p className="title-large">{title}</p>
        <p className="body-small faint ellipsis">{url}</p>
        <div className="iv-preview-actions">
          {/* Tonal: sending it on is the page's main action, in the bar below. */}
          {openable ? (
            <a className="btn iv-btn-accent iv-grow" {...outside(openable)}>
              <ExternalLink size={18} aria-hidden />
              {t('actionOpen')}
            </a>
          ) : (
            <button type="button" className="btn iv-btn-accent iv-grow" disabled>
              <ExternalLink size={18} aria-hidden />
              {t('actionOpen')}
            </button>
          )}
          <button
            type="button"
            className="btn btn-outlined btn-square"
            aria-label={t('actionCopy')}
            title={t('actionCopy')}
            onClick={() => copy(url)}
          >
            <Copy size={18} aria-hidden />
          </button>
        </div>
      </div>
    </div>
  );
}

// ---- task.v1 ----

function TaskView({ p, workspaceName }: { p: PayloadOf<'task'>; workspaceName?: string }) {
  const t = useT();
  const s = p.source;
  const kind = sourceKind(s.type, s.payload);
  const label = itemKindLabel(t, { kind, payload: s.payload });
  const card = (
    <>
      <span className="iv-kind-box">
        <KindGlyph kind={kind} size={20} />
      </span>
      <span className="iv-grow iv-min0">
        <span className="body-small iv-block">{s.employee ? t('taskFrom', { employee: s.employee, kind: label }) : label}</span>
        <span className="title-small iv-clamp">{s.title}</span>
      </span>
    </>
  );
  return (
    <div>
      {p.note && (
        <ItemSection title={t('sectionYourRequest')}>
          <div className="panel iv-note">
            <Quote size={20} aria-hidden />
            <p className="body-large pre-wrap">{p.note}</p>
          </div>
        </ItemSection>
      )}
      <ItemSection title={t('sectionHandedOffWork')}>
        {s.itemId ? (
          <Link to={Routes.item(s.itemId)} className="card iv-row-card">
            {card}
            <ChevronRight size={20} className="faint" aria-hidden />
          </Link>
        ) : (
          <div className="card iv-row-card">{card}</div>
        )}
        {/* The work as the person saw it, quoted under its card. */}
        <div className="iv-embedded">
          <PayloadView payload={s.payload} workspaceName={workspaceName} embedded />
        </div>
      </ItemSection>
    </div>
  );
}

// ---- access_request.v1 ----

function AccessRequestView({ p, requester }: { p: PayloadOf<'access_request'>; requester?: EmployeeSummary }) {
  const t = useT();
  const name = p.requestedBy;
  const kind = accessKind(t, p.kind);
  const duration = p.validMinutes === undefined ? t('accessOnce') : t('accessDuration', { minutes: p.validMinutes });
  const row = (label: string, value: ReactNode) => (
    <div className="iv-access-row">
      <div className="overline faint">{label}</div>
      {value}
    </div>
  );
  return (
    <div>
      <div className="panel iv-access">
        <div className="iv-access-head">
          {requester ? (
            <EmployeeAvatar avatar={requester.avatar} color={requester.color} label={requester.name} />
          ) : (
            <span className="iv-access-key">
              <KeyRound size={22} aria-hidden />
            </span>
          )}
          <div className="iv-min0">
            <p className="title-medium">{name ? t('accessAsks', { name }) : t('accessAsksAnon')}</p>
            <p className="body-small iv-inline-icon">
              <Timer size={14} aria-hidden />
              {duration}
            </p>
          </div>
        </div>
        {row(
          t('accessWhat'),
          <div className="iv-pills">
            <span className="title-medium">{p.resource}</span>
            <Pill label={kind.label} icon={kind.icon} tone="warning" />
          </div>,
        )}
        {row(t('accessWhy'), <p className="body-large pre-wrap">{p.purpose}</p>)}
        {row(t('accessWhere'), <code className="iv-code">{p.target}</code>)}
        {row(t('accessHowLong'), <p className="body-large">{duration}</p>)}
        <div className="iv-access-safe">
          <ShieldCheck size={18} aria-hidden />
          <p className="body-small">{t('accessSafe', { name: name ?? 'AI' })}</p>
        </div>
      </div>
      {p.notes && <NotesBlock notes={p.notes} />}
    </div>
  );
}

// ---- inbound_message.v1 (untrusted) ----

// The per48h context fields the mobile app names (in Lithuanian there too).
const contextLabels: Record<string, string> = {
  project_title: 'Projektas',
  status: 'Būsena',
  status_lt: 'Būsena',
  first_message: 'Pirma žinutė',
  messages_before: 'Ankstesnių žinučių',
};

function InboundMessageView({
  p,
  embedded,
  letIn,
  check,
  checkAction,
}: {
  p: PayloadOf<'inbound_message'>;
  embedded: boolean;
  letIn: boolean | 'automatic';
  check?: ItemCheck;
  checkAction?: ReactNode;
}) {
  const t = useT();
  const verdict = check && checkStyle(t, check.verdict);
  const context = Object.entries(p.context).flatMap(([key, value]) => {
    const label = contextLabels[key];
    if (!label || value === null || value === undefined) return [];
    if (key === 'status' && 'status_lt' in p.context) return [];
    const shown = typeof value === 'boolean' ? (value ? 'Taip' : 'Ne') : typeof value === 'object' ? JSON.stringify(value) : String(value);
    return [[label, shown] as const];
  });
  return (
    <div>
      {!embedded && (
        <div className="iv-banner iv-gap-xl" style={{ background: 'var(--warning-soft)' }}>
          <ShieldAlert size={20} color="var(--warning)" aria-hidden />
          <div className="iv-grow iv-min0">
            <p className="body-medium">{letIn === 'automatic' ? t('untrustedNoticeLetInAuto') : letIn ? t('untrustedNoticeLetIn') : t('untrustedNotice')}</p>
            {check && verdict && (
              <div className="iv-check" data-testid="check">
                <Pill label={verdict.label} tone={verdict.tone} icon={verdict.icon} />
                {check.reason && <p className="body-small iv-check-reason">{t('checkReason', { reason: check.reason })}</p>}
              </div>
            )}
            {checkAction}
          </div>
        </div>
      )}
      <ItemSection title={t('sectionFrom')}>
        <div className="panel">
          <ContactLines contact={p.from} />
        </div>
      </ItemSection>
      <ItemSection title={t('sectionMessage')}>
        <div className="panel">
          {p.subject && <p className="title-medium iv-subject">{p.subject}</p>}
          {/* Plain text on purpose: no links, no markdown, nothing clickable. */}
          <p className="body-large pre-wrap" data-testid="untrusted-text">
            {p.text}
          </p>
        </div>
      </ItemSection>
      {context.length > 0 && (
        <ItemSection title={t('sectionContext')}>
          <dl className="panel iv-context">
            {context.map(([label, value]) => (
              <div key={label}>
                <dt className="body-small">{label}</dt>
                <dd className="body-medium pre-wrap">{value}</dd>
              </div>
            ))}
          </dl>
        </ItemSection>
      )}
    </div>
  );
}

// ---- report.v1 ----

/** A report's period as written, or as a date when an employee sent a timestamp. */
function usePeriod() {
  const dates = useDates();
  return (period: string) => {
    if (!/^\d{4}-\d{2}-\d{2}(T[\d:.]+(Z|[+-]\d{2}:?\d{2})?)?$/.test(period)) return period;
    const at = new Date(period.length === 10 ? `${period}T00:00:00` : period);
    return Number.isNaN(at.getTime()) ? period : dates.day(at);
  };
}

function ReportView({ p }: { p: PayloadOf<'report'> }) {
  const period = usePeriod();
  return (
    <div>
      {p.period && (
        <div className="iv-gap-md">
          <Pill label={period(p.period)} icon={CalendarRange} />
        </div>
      )}
      <Metrics metrics={p.metrics} />
      <div className="iv-gap-xl">
        <MiniMarkdown text={p.text} />
      </div>
    </div>
  );
}

/** The numbers behind a report or a change, as tiles; a falling number in red. */
function Metrics({ metrics }: { metrics: Metric[] }) {
  const t = useT();
  if (metrics.length === 0) return null;
  return (
    <ItemSection title={t('sectionMetrics')}>
      <div className="iv-metrics">
        {metrics.map((m, i) => (
          <div key={i} className="iv-metric">
            <span className="headline-small">{m.value}</span>
            <span className="body-small">{m.label}</span>
            {m.delta && (
              <span className="label-small" style={{ color: m.delta.startsWith('-') ? 'var(--danger)' : 'var(--success)' }}>
                {m.delta}
              </span>
            )}
          </div>
        ))}
      </div>
    </ItemSection>
  );
}

// ---- ad_change.v1 ----

/** A change to a paid campaign: where, what changes (the headline), the money, and why. Approving
 * spends money, so nothing here is edited: the person sends it back instead. */
function AdChangeView({ p }: { p: PayloadOf<'ad_change'> }) {
  const t = useT();
  const locale = useLocale();
  const b = p.budget;
  const money = (n: number) => (b ? formatCurrency(locale, n, b.currency) : '');
  const per = (amount: string) =>
    b?.period === 'day' ? t('adBudgetDay', { amount }) : b?.period === 'month' ? t('adBudgetMonth', { amount }) : t('adBudgetTotal', { amount });
  return (
    <div>
      <div className="panel iv-ad">
        <div className="iv-ad-head">
          <span className="iv-ad-icon" aria-hidden>
            <Megaphone size={20} />
          </span>
          <div className="iv-min0">
            <p className="overline faint">{t('adCampaign')}</p>
            <p className="title-medium">{p.campaign}</p>
          </div>
          <Pill label={adPlatformName(t, p.platform)} background="var(--sunken)" color="var(--ink)" />
        </div>
        <div className="iv-ad-change">
          <p className="overline faint">{t('adChange')}</p>
          <p className="headline-small">{p.change}</p>
        </div>
        {b && (
          <div className="iv-ad-budget">
            <p className="overline faint">{t('adBudget')}</p>
            <p className="iv-ad-amounts">
              {b.from !== undefined && (
                <>
                  <span className="title-medium iv-ad-from">{money(b.from)}</span>
                  <ArrowRight size={18} aria-hidden className="faint" />
                </>
              )}
              <span className="title-large">{per(money(b.to))}</span>
            </p>
          </div>
        )}
      </div>
      {p.reason && (
        <ItemSection title={t('adReason')}>
          <p className="body-large pre-wrap">
            <LinkedText text={p.reason} />
          </p>
        </ItemSection>
      )}
      {p.expected && (
        <ItemSection title={t('adExpected')}>
          <div className="panel iv-note iv-ad-expected">
            <TrendingUp size={18} aria-hidden />
            <p className="body-medium pre-wrap">{p.expected}</p>
          </div>
        </ItemSection>
      )}
      <Metrics metrics={p.metrics} />
      {p.adText && (
        <ItemSection title={t('adText')} trailing={<CopyButton text={p.adText} />}>
          <div className="panel">
            <p className="body-large pre-wrap">{p.adText}</p>
          </div>
        </ItemSection>
      )}
    </div>
  );
}

// ---- document.v1 ----

/** Written work to send or publish (an invoice, a description, a job ad): who it is for, where it
 * goes, and the text itself. Editing changes the title and the text. */
function DocumentView({ p, onChange }: { p: PayloadOf<'document'>; onChange?: (p: ItemPayload) => void }) {
  const t = useT();
  const u = safeUrl(p.url);
  const label = t('typeDocument');
  return (
    <div>
      {hasContact(p.for) && (
        <ItemSection title={t('sectionTo')}>
          <div className="panel">
            <ContactLines contact={p.for} />
          </div>
        </ItemSection>
      )}
      {u && (
        <ItemSection title={t('sectionLinks')}>
          <TileList>
            <LinkTile label={prettyUrl(u)} url={u} />
          </TileList>
        </ItemSection>
      )}
      <ItemSection title={label} trailing={onChange ? undefined : <CopyButton text={`${p.title}\n\n${p.body}`} />}>
        <article className="panel iv-paper">
          {onChange ? (
            <>
              <input
                className="input title-large"
                value={p.title}
                aria-label={t('documentTitle')}
                onChange={(e) => onChange({ ...p, title: e.target.value })}
              />
              <textarea
                className="textarea iv-edit body-medium"
                rows={12}
                value={p.body}
                aria-label={t('documentBody')}
                onChange={(e) => onChange({ ...p, body: e.target.value })}
              />
            </>
          ) : (
            <>
              <h3 className="title-large">{p.title}</h3>
              <MiniMarkdown text={p.body} />
            </>
          )}
        </article>
      </ItemSection>
    </div>
  );
}
